import _get from 'lodash/get';
import * as Yup from 'yup';
import { TFunction } from 'i18next';
import { FieldItem, Values } from '@lib/interfaces/form';
import { getAttachmentsValidation } from '@lib/utils/validationUtils';
import Input from '@lib/components/Input/Input';
import { InputTypes } from '@lib/components/Input/enums';
import Select from '@lib/components/Select/Select';
import MultiSelectValueContainer from '@lib/components/Select/components/MultiSelectValueContainer';
import FilesUploader from '@lib/components/FilesUploader/FilesUploader';
import { FilesPreview } from '@lib/components/FilesUploader/enums';
import RadioButtonGroup, {
  RadioButtonWrapViews,
} from '@lib/components/RadioButtonGroup/RadioButtonGroup';
import RadioButtonAsButton from '@lib/components/RadioButtonAsButton/RadioButtonAsButton';
import Typography from '@lib/components/Typography/Typography';
import FieldTitleRender from '@lib/components/FieldTitleRender/FieldTitleRender';
import SupportiveMessage, {
  SupportiveMessageColor,
  SupportiveMessageType,
} from '@lib/components/SupportiveMessage/SupportiveMessage';
import {
  FileSizeLimits,
  UppyDocumentationImportAllowedFileTypes,
  UppyZipArchiveAllowedFileTypes,
  videoSizeLimit,
} from '@lib/enums/mimeTypes';
import {
  Steps,
  MAX_IMPORT_FILES,
  MAX_ARCHIVE_FILE_SIZE_MB,
  MAX_ARCHIVE_FILE_SIZE,
  FTP_URL_REGEX,
} from '@lib/enums/form';
import { ButtonSizes } from '@lib/components/Button/Button';
import DocumentImportFormFieldNames, {
  DocumentImportAiEnhancementOption,
} from '@lib/enums/fieldNames/documentImportFormFieldNames';
import DocumentFormFieldNames from '@lib/enums/fieldNames/documentFormFieldNames';
import {
  DocumentationImportKindEnum,
  EdmDocumentRenewalReminderTemplateFragmentFragment,
} from 'graphql-common';
import { GetLoadOptions, SelectOption } from '@lib/components/Select/types';
import AiCreditsIndicator, {
  AiCredits,
} from 'components/AiAssistant/AiCreditsIndicator';
import getCategoryFields from '../../DocumentForm/utils/getCategoryFields';
import getAccessValidationFields from '../../DocumentForm/utils/getAccessValidationFields';
import getOperationalScopeFields from '../../DocumentForm/utils/getOperationalScopeFields';
import getLifecycleFields from '../../DocumentForm/utils/getLifecycleFields';
import AiAssistanceTeaser from '../components/AiAssistanceTeaser';
import AiImportInfoMessage from '../components/AiImportInfoMessage';
import SourceKindCaption from '../components/SourceKindCaption';
import FtpPreviewBlock from '../components/FtpPreviewBlock';
import CountersFooter from '../components/CountersFooter';

type Args = {
  t: TFunction<'translation', undefined>;
  approverLoadOptions: GetLoadOptions;
  areasLoadOptions: GetLoadOptions;
  categoriesLoadOptions: GetLoadOptions;
  equipmentLoadOptions: GetLoadOptions;
  formTemplatesLoadOptions: GetLoadOptions;
  sectorsLoadOptions: GetLoadOptions;
  sitesLoadOptions: GetLoadOptions;
  tasksLoadOptions: GetLoadOptions;
  renewalReminderTemplate?: EdmDocumentRenewalReminderTemplateFragmentFragment | null;
  openEquipmentModal?: () => void;
  openAreasModal?: () => void;
  openFormTemplatesModal?: () => void;
  openTasksModal?: () => void;
  isFullAdmin?: boolean;
  isPlanLocked?: boolean;
  hasCategoriesAccess?: boolean;
  hasSitesAccess?: boolean;
  hasAssetsAccess?: boolean;
  hasTasksAccess?: boolean;
  hasUsersAccess?: boolean;
  // AI prefill options are shown only when the company plan grants AiAssistance
  // (Write).
  isAiEnabled?: boolean;
  aiCredits?: AiCredits | null;
  // Copy for why AI spend is refused right now (null while it is admitted).
  // `documentationImportCreate` is budget-gated like every other AI entry
  // point: ticking an enhancement without budget fails the mutation AND
  // creates no import at all, so the control is disabled rather than let the
  // user lose the whole import to it. An import with no enhancement ticked is
  // never gated — the files themselves aren't AI work.
  aiBlockedText?: string | null;
};

// Options offered by the AI processing multi-select — mirrors
// DocumentationImportCreateInputObject's detect* flags, in the order shown
// in the dropdown.
const AI_ENHANCEMENT_OPTIONS: {
  value: DocumentImportAiEnhancementOption;
  labelKey: string;
}[] = [
  {
    value: DocumentImportAiEnhancementOption.DetectName,
    labelKey: 'documents-import-ai-generate-names',
  },
  {
    value: DocumentImportAiEnhancementOption.DetectCategory,
    labelKey: 'documents-import-ai-detect-category',
  },
  {
    value: DocumentImportAiEnhancementOption.DetectDescription,
    labelKey: 'documents-import-ai-generate-descriptions',
  },
  {
    value: DocumentImportAiEnhancementOption.DetectCode,
    labelKey: 'documents-import-ai-detect-codes',
  },
  {
    value: DocumentImportAiEnhancementOption.DetectTags,
    labelKey: 'documents-import-ai-generate-tags',
  },
];

const getSourceKindValue = (values: Values) =>
  _get(values, [DocumentImportFormFieldNames.SourceKind, 'value']) as
    DocumentationImportKindEnum | undefined;

const isFiles = (values: Values) =>
  getSourceKindValue(values) === DocumentationImportKindEnum.Files;

const isArchive = (values: Values) =>
  getSourceKindValue(values) === DocumentationImportKindEnum.Archive;

const isFtp = (values: Values) =>
  getSourceKindValue(values) === DocumentationImportKindEnum.Ftp;

const getAiEnhancements = (values: Values) =>
  (_get(values, DocumentImportFormFieldNames.AiEnhancements) ||
    []) as SelectOption[];

const isAnyAiOptionSelected = (values: Values) =>
  !!getAiEnhancements(values).length;

const hasAiEnhancement = (
  values: Values,
  option: DocumentImportAiEnhancementOption,
) => getAiEnhancements(values).some((item) => item.value === option);

export default function getDocumentImportFormFields({
  t,
  approverLoadOptions,
  areasLoadOptions,
  categoriesLoadOptions,
  equipmentLoadOptions,
  formTemplatesLoadOptions,
  sectorsLoadOptions,
  sitesLoadOptions,
  tasksLoadOptions,
  renewalReminderTemplate,
  openEquipmentModal,
  openAreasModal,
  openFormTemplatesModal,
  openTasksModal,
  isFullAdmin = true,
  isPlanLocked,
  hasCategoriesAccess = true,
  hasSitesAccess = true,
  hasAssetsAccess = true,
  hasTasksAccess = true,
  hasUsersAccess = true,
  isAiEnabled = false,
  aiCredits,
  aiBlockedText,
}: Args): FieldItem[] {
  return [
    // ── STEP 1: Upload Files ──────────────────────────────
    {
      name: 'uploadMethodTitle',
      step: Steps.step1,
      customRender: () => (
        <Typography variant="h3" key="uploadMethodTitle" block>
          {t('documents-import-select-method')}
        </Typography>
      ),
    },
    {
      name: DocumentImportFormFieldNames.SourceKind,
      step: Steps.step1,
      element: RadioButtonGroup,
      componentProps: {
        RadioButtonComponent: RadioButtonAsButton,
        radioButtonComponentProps: {
          buttonSize: ButtonSizes.small,
        },
        wrapView: RadioButtonWrapViews.row,
        options: [
          {
            value: DocumentationImportKindEnum.Files,
            label: t('documents-import-source-files'),
          },
          {
            value: DocumentationImportKindEnum.Archive,
            label: t('documents-import-source-archive'),
          },
          {
            value: DocumentationImportKindEnum.Ftp,
            label: t('documents-import-source-ftp'),
          },
        ],
      },
      getDescription: (values: Values) => {
        const kind = getSourceKindValue(values);
        if (!kind) return null;
        return <SourceKindCaption sourceKind={kind} />;
      },
    },
    // FILES uploader
    {
      name: DocumentImportFormFieldNames.Files,
      step: Steps.step1,
      isVisibleFn: isFiles,
      element: FilesUploader,
      validation: Yup.array().when(
        DocumentImportFormFieldNames.SourceKind,
        (deps, schema) => {
          const kind = _get(deps, [0, 'value']);
          return kind === DocumentationImportKindEnum.Files
            ? getAttachmentsValidation({ required: true, t })
            : schema.nullable();
        },
      ),
      componentProps: {
        filesView: FilesPreview.Documents,
        allowedFileTypes: UppyDocumentationImportAllowedFileTypes,
        maxFileSize: videoSizeLimit,
        maxNumberOfFiles: MAX_IMPORT_FILES,
        note: t('documents-import-files-upload-note', {
          sizeImg: FileSizeLimits.Image,
          sizePdf: FileSizeLimits.File,
          sizeVideo: FileSizeLimits.Video,
        }),
        uploadButtonProps: {
          buttonText: t('upload-files'),
          buttonSize: ButtonSizes.large,
          leftIcon: 'upload',
          fullWidth: true,
        },
      },
    },
    // ARCHIVE uploader (single ZIP)
    {
      name: DocumentImportFormFieldNames.Archive,
      step: Steps.step1,
      isVisibleFn: isArchive,
      element: FilesUploader,
      validation: Yup.array().when(
        DocumentImportFormFieldNames.SourceKind,
        (deps, schema) => {
          const kind = _get(deps, [0, 'value']);
          return kind === DocumentationImportKindEnum.Archive
            ? getAttachmentsValidation({ required: true, t })
            : schema.nullable();
        },
      ),
      componentProps: {
        filesView: FilesPreview.Documents,
        allowedFileTypes: UppyZipArchiveAllowedFileTypes,
        maxFileSize: MAX_ARCHIVE_FILE_SIZE,
        maxNumberOfFiles: 1,
        note: t('documents-import-archive-upload-note', {
          size: MAX_ARCHIVE_FILE_SIZE_MB,
        }),
        uploadButtonProps: {
          buttonText: t('upload-zip-archive'),
          buttonSize: ButtonSizes.large,
          leftIcon: 'upload',
          fullWidth: true,
        },
      },
    },
    // FTP fields
    {
      name: DocumentImportFormFieldNames.FtpUrl,
      label: t('ftp-url'),
      step: Steps.step1,
      isVisibleFn: isFtp,
      element: Input,
      validation: Yup.string().when(
        DocumentImportFormFieldNames.SourceKind,
        (deps, schema) => {
          const kind = _get(deps, [0, 'value']);
          return kind === DocumentationImportKindEnum.Ftp
            ? schema
                .trim()
                .required(t('required-field-error'))
                .matches(FTP_URL_REGEX, t('documents-import-ftp-invalid-url'))
            : schema.nullable();
        },
      ),
      isOptionalFn: () => false,
    },
    {
      name: DocumentImportFormFieldNames.FtpUsername,
      label: t('username'),
      step: Steps.step1,
      isVisibleFn: isFtp,
      isOptionalFn: () => true,
      element: Input,
    },
    {
      name: DocumentImportFormFieldNames.FtpPassword,
      label: t('password'),
      step: Steps.step1,
      isVisibleFn: isFtp,
      isOptionalFn: () => true,
      element: Input,
      componentProps: {
        type: InputTypes.password,
      },
    },
    {
      name: 'ftpPreviewBlock',
      step: Steps.step1,
      isVisibleFn: isFtp,
      customRender: () => <FtpPreviewBlock key="ftpPreviewBlock" />,
    },
    // ── STEP 2: Shared Metadata ───────────────────────────
    {
      name: 'sharedMetadataInfo',
      step: Steps.step2,
      customRender: () => (
        <SupportiveMessage
          key="sharedMetadataInfo"
          color={SupportiveMessageColor.Blue}
          type={SupportiveMessageType.FilledWithLeftBorder}
          text={t('documents-import-shared-metadata-info')}
          fullWidth
        />
      ),
    },
    // ── STEP 2: AI processing (first field, only when AiAssistance is
    // enabled) — mapped to the import input's nested detectFields
    // (name/category/description/code/codePrompt/tags) in
    // getPreparedImportData.
    {
      name: 'aiAssistanceTeaser',
      step: Steps.step2,
      isVisibleFn: () => !isAiEnabled,
      customRender: () => <AiAssistanceTeaser key="aiAssistanceTeaser" />,
    },
    {
      name: 'aiProcessingTitle',
      step: Steps.step2,
      isVisibleFn: () => isAiEnabled,
      customRender: () => (
        <FieldTitleRender
          key="aiProcessingTitle"
          title={t('documents-import-ai-processing')}
          tooltipText={t('documents-import-ai-processing-tooltip')}
        />
      ),
    },
    {
      name: DocumentImportFormFieldNames.AiEnhancements,
      step: Steps.step2,
      isVisibleFn: () => isAiEnabled,
      isOptionalFn: () => false,
      label: t('documents-import-ai-enhancements'),
      element: Select,
      componentProps: {
        isMulti: true,
        isClearable: false,
        isSearchable: false,
        disabled: !!aiBlockedText,
        // Why the field can't be used, on hover of the disabled control
        // itself — the description below it is easy to miss when the user is
        // aiming straight at the input.
        disabledTooltipProps: aiBlockedText
          ? { body: aiBlockedText }
          : undefined,
        maxMenuHeight: 400,
        usePortal: true,
        options: AI_ENHANCEMENT_OPTIONS.map(({ value, labelKey }) => ({
          value,
          label: t(labelKey),
        })),
        components: {
          ValueContainer: MultiSelectValueContainer,
        },
      },
      // The block reason replaces the credits line — with the select disabled,
      // a remaining-credits count answers nothing.
      description: aiBlockedText || <AiCreditsIndicator credits={aiCredits} />,
    },
    // Code-format hint — shown only while "Auto-detect document codes" is
    // selected in the AI processing multi-select.
    {
      name: DocumentImportFormFieldNames.AiCodeInstructions,
      step: Steps.step2,
      isVisibleFn: (values: Values) =>
        isAiEnabled &&
        hasAiEnhancement(values, DocumentImportAiEnhancementOption.DetectCode),
      label: t('documents-import-ai-code-instructions-label'),
      element: Input,
      componentProps: {
        type: InputTypes.textarea,
      },
      description: t('documents-import-ai-code-instructions-note'),
    },
    {
      name: 'aiImportInfoMessage',
      step: Steps.step2,
      isVisibleFn: (values: Values) =>
        isAiEnabled && isAnyAiOptionSelected(values),
      customRender: () => <AiImportInfoMessage key="aiImportInfoMessage" />,
    },
    ...getCategoryFields({
      categoriesLoadOptions,
      hasCategoriesAccess,
      isFullAdmin,
      isPlanLocked,
      showTitle: true,
      t,
      withPermissionsFields: true,
    }).map((f) => {
      // When "Auto-detect document category" is selected, AI fills the
      // category — disable manual selection and explain how to re-enable it.
      if (f.name !== DocumentFormFieldNames.Category) {
        return { ...f, step: Steps.step2 };
      }
      return {
        ...f,
        step: Steps.step2,
        getComponentProps: (values: Values) =>
          hasAiEnhancement(
            values,
            DocumentImportAiEnhancementOption.DetectCategory,
          )
            ? {
                disabled: true,
                disabledTooltipProps: {
                  body: t('documents-import-category-ai-detected-tooltip'),
                },
              }
            : undefined,
      };
    }),
    ...getAccessValidationFields({
      t,
      sectorsLoadOptions,
      sitesLoadOptions,
      approverLoadOptions,
      isFullAdmin,
      isPlanLocked,
      hasSitesAccess,
      hasUsersAccess,
    }).map((f) => ({ ...f, step: Steps.step2 })),
    ...getOperationalScopeFields({
      t,
      equipmentLoadOptions,
      areasLoadOptions,
      formTemplatesLoadOptions,
      tasksLoadOptions,
      openEquipmentModal,
      openAreasModal,
      openFormTemplatesModal,
      openTasksModal,
      isFullAdmin,
      isPlanLocked,
      hasAssetsAccess,
      hasSitesAccess,
      hasTasksAccess,
    }).map((f) => ({ ...f, step: Steps.step2 })),
    ...getLifecycleFields({
      t,
      template: renewalReminderTemplate,
      hasPriorReminderValues: false,
      isFullAdmin,
      isPlanLocked,
    }).map((f) => ({ ...f, step: Steps.step2 })),
    {
      name: 'countersFooter',
      step: Steps.step2,
      customRender: (values: Values) => (
        <CountersFooter key="countersFooter" values={values} />
      ),
    },
    {
      name: 'lifecycleTitleSpacer',
      step: Steps.step2,
      customRender: () => <FieldTitleRender key="spacer" title="" />,
    },
  ];
}
